import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  Button,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Translate as TranslateIcon,
  Language as LanguageIcon,
  Save as SaveIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { SUPPORTED_LANGUAGES, translationService } from '../services/translationService';

export const TranslationSettings: React.FC = () => {
  const theme = useTheme();
  const { settings, updateTranslationSettings, loading } = useUserSettings();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [localSettings, setLocalSettings] = useState(settings.translation);

  React.useEffect(() => {
    setLocalSettings(settings.translation);
  }, [settings.translation]);

  const handleSettingChange = (setting: keyof typeof localSettings, value: any) => {
    setLocalSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess(false);
      setError(null);

      await updateTranslationSettings(localSettings);
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving translation settings:', err);
      setError('Failed to save translation settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDetectLanguage = () => {
    const detectedLang = translationService.getUserLanguage();
    handleSettingChange('targetLanguage', detectedLang);
  };

  const getLanguageDisplayName = (code: string) => {
    return SUPPORTED_LANGUAGES[code as keyof typeof SUPPORTED_LANGUAGES] || code;
  };

  return (
    <Card elevation={3} sx={{ mb: 4 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <TranslateIcon sx={{ mr: 1 }} />
            Translation Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure automatic translation for community chat messages.
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Translation settings updated successfully!
          </Alert>
        )}

        {/* Enable Translation */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.enabled}
                onChange={(e) => handleSettingChange('enabled', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Enable Translation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Show translation options in the community chat.
                </Typography>
              </Box>
            }
          />
        </Box>

        {localSettings.enabled && (
          <>
            {/* Target Language */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Preferred Language</InputLabel>
                <Select
                  value={localSettings.targetLanguage}
                  label="Preferred Language"
                  onChange={(e) => handleSettingChange('targetLanguage', e.target.value)}
                  startAdornment={<LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                >
                  <MenuItem value="auto">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AutoAwesomeIcon sx={{ mr: 1, fontSize: 18 }} />
                      Auto-detect from browser
                    </Box>
                  </MenuItem>
                  {Object.entries(SUPPORTED_LANGUAGES)
                    .filter(([code]) => code !== 'auto')
                    .map(([code, name]) => (
                      <MenuItem key={code} value={code}>
                        {name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              
              {localSettings.targetLanguage === 'auto' && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Detected language:
                  </Typography>
                  <Chip 
                    size="small" 
                    label={getLanguageDisplayName(translationService.getUserLanguage())}
                    color="primary"
                    variant="outlined"
                  />
                  <Button
                    size="small"
                    onClick={handleDetectLanguage}
                    sx={{ ml: 1 }}
                  >
                    Use This Language
                  </Button>
                </Box>
              )}
            </Box>

            {/* Auto-translate */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSettings.autoTranslate}
                    onChange={(e) => handleSettingChange('autoTranslate', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Auto-translate Messages
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automatically translate incoming messages to your preferred language.
                    </Typography>
                  </Box>
                }
              />
            </Box>



            {/* Information Box */}
            <Box sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', 
              borderRadius: 1, 
              mb: 3 
            }}>
              <Typography variant="body2" color="text.secondary">
                <strong>How it works:</strong> Messages in the community chat will show a translate button. 
                If auto-translate is enabled, foreign language messages will be automatically translated to your preferred language.
                Translation is powered by Google Cloud Translation API and cached for performance.
              </Typography>
            </Box>
          </>
        )}

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ minWidth: 120 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}; 